import { CONTENT_PLATFORM_PROPERTY_NAME, contentIsPublishedWithScheduledUpdate, contentIsScheduled, getContentPropertyValue, useAuthInfoProvider } from 'content-management-data-lib';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { Link } from 'foundations-components/transitional/components/Link';
import { ContentStatusTags } from '../components/headerDetails/ContentStatusTag';
import { TemplateRowMessage } from '../components/headerDetails/HeaderDetailRows';
import AssociatedFormLabel from '../components/headerDetails/AssociatedFormLabel';
import { CampaignLabel } from 'campaigns-lib/campaignsPlatform';
import ContentRoutes from 'ContentUtils/Routes';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const updateConfigToHaveBooleanHiddenProperty = ({
  Title,
  Description,
  hidden
}, contentObject, isUngatedToUseResolvedStateProperty) => {
  const isHidden = hidden !== undefined && (typeof hidden === 'boolean' && hidden || typeof hidden === 'function' && hidden(contentObject, isUngatedToUseResolvedStateProperty));
  return {
    Title,
    Description,
    hidden: isHidden
  };
};
const mergeDetailsRenderConfig = (detailsConfigOverrides, defaultConfig, contentObject, isUngatedToUseResolvedStateProperty) => {
  const defaultConfigWithBooleanHiddenProperty = Object.keys(defaultConfig).reduce((acc, propertyName) => {
    acc[propertyName] = updateConfigToHaveBooleanHiddenProperty(defaultConfig[propertyName], contentObject, isUngatedToUseResolvedStateProperty);
    return acc;
  }, {});
  return Object.keys(detailsConfigOverrides).reduce((acc, propertyName) => {
    const overridenRenderConfig = detailsConfigOverrides[propertyName];
    const hasDefaultConfig = (propertyName in defaultConfig);
    const config = hasDefaultConfig ? Object.assign({}, defaultConfig[propertyName], overridenRenderConfig) : overridenRenderConfig;
    if (config.Title && config.Description) {
      const updatedConfig = updateConfigToHaveBooleanHiddenProperty(config, contentObject, isUngatedToUseResolvedStateProperty);
      acc[propertyName] = updatedConfig;
    }
    return acc;
  }, defaultConfigWithBooleanHiddenProperty);
};
const DEFAULT_PANEL_RENDER_MESSAGE_CONFIG = {
  [CONTENT_PLATFORM_PROPERTY_NAME.NAME]: {
    Title: () => /*#__PURE__*/_jsx(FormattedMessage, {
      message: `content-management-details-lib.header.headerDetails.titles.${CONTENT_PLATFORM_PROPERTY_NAME.NAME}`
    }),
    Description: ({
      contentObject
    }) => {
      const name = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.NAME);
      return name ? /*#__PURE__*/_jsx(_Fragment, {
        children: name
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: `content-management-details-lib.header.headerDetails.missingValues.${CONTENT_PLATFORM_PROPERTY_NAME.NAME}`
      });
    }
  },
  [CONTENT_PLATFORM_PROPERTY_NAME.HTML_TITLE]: {
    Title: () => /*#__PURE__*/_jsx(FormattedMessage, {
      message: `content-management-details-lib.header.headerDetails.titles.${CONTENT_PLATFORM_PROPERTY_NAME.HTML_TITLE}`
    }),
    Description: ({
      contentObject
    }) => {
      const title = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.HTML_TITLE);
      return title ? /*#__PURE__*/_jsx(_Fragment, {
        children: title
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: `content-management-details-lib.header.headerDetails.missingValues.${CONTENT_PLATFORM_PROPERTY_NAME.HTML_TITLE}`
      });
    }
  },
  [CONTENT_PLATFORM_PROPERTY_NAME.STATE]: {
    Title: () => /*#__PURE__*/_jsx(FormattedMessage, {
      message: `content-management-details-lib.header.headerDetails.titles.${CONTENT_PLATFORM_PROPERTY_NAME.STATE}`
    }),
    Description: ContentStatusTags
  },
  [CONTENT_PLATFORM_PROPERTY_NAME.UPDATED_AT]: {
    Title: () => /*#__PURE__*/_jsx(FormattedMessage, {
      message: `content-management-details-lib.header.headerDetails.titles.${CONTENT_PLATFORM_PROPERTY_NAME.UPDATED_AT}`
    }),
    Description: ({
      contentObject
    }) => {
      const updatedAt = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.UPDATED_AT);
      return /*#__PURE__*/_jsx(FormattedDateTime, {
        format: "LLL",
        type: "portalTz",
        value: updatedAt
      });
    }
  },
  [CONTENT_PLATFORM_PROPERTY_NAME.CREATED_AT]: {
    Title: () => /*#__PURE__*/_jsx(FormattedMessage, {
      message: `content-management-details-lib.header.headerDetails.titles.${CONTENT_PLATFORM_PROPERTY_NAME.CREATED_AT}`
    }),
    Description: ({
      contentObject
    }) => {
      const createdAt = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.CREATED_AT);
      return /*#__PURE__*/_jsx(FormattedDateTime, {
        format: "LLL",
        type: "portalTz",
        value: createdAt
      });
    }
  },
  [CONTENT_PLATFORM_PROPERTY_NAME.ABSOLUTE_URL]: {
    Title: () => /*#__PURE__*/_jsx(FormattedMessage, {
      message: `content-management-details-lib.header.headerDetails.titles.${CONTENT_PLATFORM_PROPERTY_NAME.ABSOLUTE_URL}`
    }),
    Description: ({
      contentObject
    }) => {
      const url = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.ABSOLUTE_URL);
      return /*#__PURE__*/_jsx(Link, {
        external: true,
        href: url,
        truncate: true,
        children: url
      });
    }
  },
  [CONTENT_PLATFORM_PROPERTY_NAME.TEMPLATE_PATH]: {
    Title: () => /*#__PURE__*/_jsx(FormattedMessage, {
      message: `content-management-details-lib.header.headerDetails.titles.${CONTENT_PLATFORM_PROPERTY_NAME.TEMPLATE_PATH}`
    }),
    Description: TemplateRowMessage
  }
};
function getDefaultPanelRenderMessageConfig(contentObject, conditionalRowData) {
  const finalConfig = Object.assign({}, DEFAULT_PANEL_RENDER_MESSAGE_CONFIG);
  const {
    forms
  } = conditionalRowData || {};
  const contentType = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.OBJECT_TYPE);
  if (forms && forms.length > 0) {
    finalConfig.forms = {
      Title: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "content-management-details-lib.header.headerDetails.titles.forms"
      }),
      Description: AssociatedFormLabel
    };
  }
  const campaignName = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.CAMPAIGN_NAME);
  const campaignGuid = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.CAMPAIGN);
  const CampaignTitle = () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "content-management-details-lib.header.headerDetails.titles.campaign"
  });
  // TODO: remove this gross contentType check and rely totally on CampaignLabel once we have associations
  // between pages+blog posts and campaigns - https://hubspot.slack.com/archives/C068Q06TB8Q/p1749757863552739
  // Case studies works with that campaign association, so we need to support both cases for now.
  if (contentType === 'LANDING_PAGE' || contentType === 'SITE_PAGE' || contentType === 'BLOG_POST') {
    if (campaignName && campaignGuid) {
      finalConfig.campaigns = {
        Title: CampaignTitle,
        Description: () => {
          return /*#__PURE__*/_jsx(Link, {
            href: ContentRoutes.campaignDetails(campaignGuid),
            external: true,
            children: campaignName
          });
        }
      };
    }
  } else {
    finalConfig.campaigns = {
      Title: CampaignTitle,
      Description: () => {
        return /*#__PURE__*/_jsx(CampaignLabel, {
          objectType: getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.OBJECT_TYPE),
          assetId: getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.CRM_OBJECT_ID)
        });
      }
    };
  }
  return finalConfig;
}
export const mergeDetailsRenderPanelConfig = (detailsPanelConfigOverrides, contentObject, isUngatedToUseResolvedStateProperty, conditionalRowData) => mergeDetailsRenderConfig(detailsPanelConfigOverrides, getDefaultPanelRenderMessageConfig(contentObject, conditionalRowData), contentObject, isUngatedToUseResolvedStateProperty);
const DEFAULT_HEADER_RENDER_MESSAGE_CONFIG = {
  [CONTENT_PLATFORM_PROPERTY_NAME.PUBLISH_DATE]: {
    Title: ({
      contentObject
    }) => {
      const {
        getIsUngatedToUseResolvedStateProperty
      } = useAuthInfoProvider();
      const isUngatedToUseResolvedStateProperty = getIsUngatedToUseResolvedStateProperty();
      const isScheduled = contentIsScheduled(contentObject, isUngatedToUseResolvedStateProperty);
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `content-management-details-lib.header.headerDetails.titles.${CONTENT_PLATFORM_PROPERTY_NAME.PUBLISH_DATE}.${isScheduled ? 'scheduled' : 'published'}`
      });
    },
    Description: ({
      contentObject
    }) => {
      const scheduledPublishDate = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE);
      const publishDate = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.PUBLISH_DATE);
      const {
        getIsUngatedToUseResolvedStateProperty
      } = useAuthInfoProvider();
      const isUngatedToUseResolvedStateProperty = getIsUngatedToUseResolvedStateProperty();
      const isScheduled = contentIsScheduled(contentObject, isUngatedToUseResolvedStateProperty);
      const date = isScheduled ? scheduledPublishDate : publishDate;
      return /*#__PURE__*/_jsx(FormattedDateTime, {
        format: "LLL",
        type: "portalTz",
        value: date
      });
    },
    hidden: (contentCrmObject, isUngatedToUseResolvedStateProperty) => {
      const publishDate = getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.PUBLISH_DATE);
      const isScheduled = contentIsScheduled(contentCrmObject, isUngatedToUseResolvedStateProperty);
      const scheduledPublishDate = getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE);
      return isScheduled ? !scheduledPublishDate : !publishDate;
    }
  },
  [CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE]: {
    Title: () => /*#__PURE__*/_jsx(FormattedMessage, {
      message: `content-management-details-lib.header.headerDetails.titles.${CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE}`
    }),
    Description: ({
      contentObject
    }) => {
      const scheduledPublishDate = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE);
      return /*#__PURE__*/_jsx(FormattedDateTime, {
        format: "LLL",
        type: "portalTz",
        value: scheduledPublishDate
      });
    },
    hidden: (contentCrmObject, isUngatedToUseResolvedStateProperty) => {
      return !contentIsPublishedWithScheduledUpdate(contentCrmObject, isUngatedToUseResolvedStateProperty);
    }
  },
  [CONTENT_PLATFORM_PROPERTY_NAME.ABSOLUTE_URL]: {
    Title: () => /*#__PURE__*/_jsx(FormattedMessage, {
      message: `content-management-details-lib.header.headerDetails.titles.${CONTENT_PLATFORM_PROPERTY_NAME.ABSOLUTE_URL}`
    }),
    Description: ({
      contentObject
    }) => {
      const url = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.ABSOLUTE_URL);
      return /*#__PURE__*/_jsx(Link, {
        external: true,
        href: url,
        truncate: true,
        children: url
      });
    }
  }
};
export const mergeDetailsRenderHeaderConfig = (detailsConfigOverrides, contentObject, isUngatedToUseResolvedStateProperty) => mergeDetailsRenderConfig(detailsConfigOverrides, DEFAULT_HEADER_RENDER_MESSAGE_CONFIG, contentObject, isUngatedToUseResolvedStateProperty);